import { useMemo } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import { useLocale } from 'next-intl';
import type { SupportedLocale } from '@/src/i18n/routing';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { applyV3CostsAndFees, getEmbeddedV3Costs } from '@/src/lib/utils/v3ListingDetail';
import { localizeRentalContent } from '@/src/lib/api/localizedRentalContent';
import { listingDetailKeys } from './queryKeys';

export function useLocalizedRentalContent(property: PropertyDetailListing | undefined, enabled = true): PropertyDetailListing | undefined {
  const locale = useLocale() as SupportedLocale;
  const translated = useQuery({
    queryKey: listingDetailKeys.rentalContent(property?.aliasEncoded ?? '', locale),
    queryFn: async ({ signal }) => {
      if (!property?.aliasEncoded || locale !== 'es') return null;
      const sourceDescription = property.details?.fullDescription;
      const localized = await localizeRentalContent(property, locale, true, signal);
      return {
        sourceDescription,
        descriptionLocale: localized.descriptionLocale,
        specialOfferTranslations: localized.specialOfferTranslations,
        fullDescription: localized.details?.fullDescription,
        costs: getEmbeddedV3Costs(localized),
      };
    },
    enabled: enabled && locale === 'es' && !!property?.aliasEncoded,
    staleTime: 5 * 60 * 1000,
    retry: false,
    meta: {
      tags: ['listingDetail'],
      context: { api: 'hotpads-api', details: 'localize rental content' },
    },
  });
  return useMemo(() => {
    if (!property || !translated.data) return property;
    const localized = { ...property, specialOfferTranslations: translated.data.specialOfferTranslations };
    const fullDescription = translated.data.fullDescription;
    const cachedSource = translated.data.sourceDescription;
    const matchesSource = typeof cachedSource === 'string' && cachedSource.trim() === property.details?.fullDescription?.trim();
    const description = matchesSource && translated.data.descriptionLocale === 'es' && fullDescription && localized.details
      ? { ...localized, details: { ...localized.details, fullDescription }, descriptionLocale: 'es' as const }
      : { ...localized, descriptionLocale: 'en' as const };
    const costs = translated.data.costs;
    return costs ? applyV3CostsAndFees(description, costs) : description;
  }, [property, translated.data]);
}
